/**
 * Upload a file.
 *
 * Returns a Promise that resolves with an object literal containing the uploaded
 * image's url and its thumbnail url as well.
 */

import RPCClient from '@bufferapp/micro-rpc-client';
import PropTypes from 'prop-types';
import { MediaTypes } from '~/constants';
import { getFileTypeFromPath } from '~/composer/composer/utils/StringUtils';

class Uploader {
  constructor({
    errorNotifier,
    userId,
    s3UploadSignature,
    imageDimensionsKey,
  }) {
    this._xhr = null;
    this._uploadProgressSub = () => {};
    this.errorNotifier = errorNotifier;
    this.userId = userId;
    this.s3UploadSignature = s3UploadSignature;
    this.imageDimensionsKey = imageDimensionsKey;
  }

  upload(file, { transcodeVideo = true, draftId } = {}) {
    return this.uploadToS3(file)
      .then((uploadKey) =>
        this.uploadToBuffer(uploadKey, {
          transcodeVideo,
        })
      )
      .then((response) => this.formatResponse(response, file, draftId));
  }

  getProgressIterator() {
    const progressGenerator = function* () {
      while (this.isUploading()) {
        // eslint-disable-next-line no-loop-func
        yield new Promise((resolve) => {
          this._uploadProgressSub = resolve;
        });
      }
    };

    return progressGenerator.call(this);
  }

  isUploading = () => this._xhr !== null;

  uploadToS3(file) {
    const formData = new FormData();

    const { userId } = this;
    const userS3UploadSignature = this.s3UploadSignature;
    const url = `https://${userS3UploadSignature.bucket}.s3.amazonaws.com`;

    this._xhr = new XMLHttpRequest(); // Use XHR to have access to a progress callback
    this._uploadProgressSub = () => {};

    const timestamp = Date.now();
    const encodedFileName = encodeURIComponent(file.name);

    const data = [
      ['key', `${userId}/uploads/${timestamp}-${encodedFileName}`],
      ['Content-Type', file.type],
      ['acl', 'public-read'],
      ['success_action_status', userS3UploadSignature.successActionStatus],
      ['policy', userS3UploadSignature.base64Policy],
      ['X-amz-algorithm', userS3UploadSignature.algorithm],
      ['X-amz-credential', userS3UploadSignature.credentials],
      ['X-amz-date', userS3UploadSignature.date],
      ['X-amz-expires', userS3UploadSignature.expires],
      ['X-amz-signature', userS3UploadSignature.signature],
    ];

    if (userS3UploadSignature.temporarySecurityToken) {
      data.push([
        'X-amz-security-token',
        userS3UploadSignature.temporarySecurityToken,
      ]);
    }

    data.push(['file', file]);

    data.forEach(([key, val]) => formData.append(key, val));

    const promise = new Promise((resolve) => {
      this._xhr.open('POST', url, true);

      this._xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      this._xhr.addEventListener('readystatechange', () => {
        if (
          this._xhr.readyState === 4 &&
          (this._xhr.status === 200 || this._xhr.status === 201)
        ) {
          const uploadKey =
            this._xhr.responseXML.getElementsByTagName('Key')[0].textContent;
          resolve(uploadKey);

          this._xhr = null;
          this._uploadProgressSub(100);
        }
      });

      this._xhr.addEventListener('error', () => {
        this.errorNotifier({
          message:
            'Uh oh! It looks like we had trouble connecting to our servers, mind trying again?',
        });
      });

      this._xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          this._uploadProgressSub(progress);
        }
      });

      this._xhr.send(formData);
    });

    return promise;
  }

  uploadToBuffer(uploadKey, { transcodeVideo }) {
    const data = {
      key: uploadKey,
      serviceForceTranscodeVideo: transcodeVideo,
    };

    const rpc = new RPCClient({
      url: '/rpc',
      sendCredentials: 'same-origin',
    });

    return rpc
      .call('composerApiProxy', {
        url: `/i/uploads/upload_media.json`,
        args: data,
      })
      .then((response) => {
        return response;
      })
      .then((response) => {
        if (response.success === false) {
          this.errorNotifier({
            message:
              'Uh oh! It looks like we had trouble connecting to our servers, mind trying again?',
          });
        }
        return response;
      });
  }

  formatResponse(response, file, draftId) {
    let formattedResponse;
    switch (response.type) {
      case 'photo':
        if (getFileTypeFromPath(response.location) === 'gif') {
          formattedResponse = {
            type: MediaTypes.GIF,
            url: response.location,
            thumbnailUrl: response.location,
            success: response.success,
            width: response.width,
            height: response.height,
          };
        } else {
          formattedResponse = {
            type: MediaTypes.IMAGE,
            url: response.location,
            thumbnailUrl: response.location,
            success: response.success,
            width: response.width,
            height: response.height,
          };
        }
        break;

      case 'video':
        formattedResponse = {
          type: MediaTypes.VIDEO,
          uploadId: response.upload_id,
          name: response.title,
          success: response.success,
          transcodeVideo: response.transcodeVideo,
          draftId,
          file,
          videoMetadata: {
            url: response.details && response.details.location,
            size: response.details && response.details.file_size,
            width: response.details && response.details.width,
            height: response.details && response.details.height,
            duration: response.details && response.details.duration,
            durationMs: response.details && response.details.duration_millis,
            frameRate: response.details && response.details.frame_rate,
            extension: response.details && response.details.file_extension,
          },
        };
        break;

      default:
        formattedResponse = {};
        break;
    }

    return formattedResponse;
  }
}

Uploader.propTypes = {
  errorNotifier: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  s3UploadSignature: PropTypes.shape({
    bucket: PropTypes.string,
    successActionStatus: PropTypes.string,
    base64Policy: PropTypes.string,
    algorithm: PropTypes.string,
    credentials: PropTypes.string,
    date: PropTypes.string,
    expires: PropTypes.string,
    signature: PropTypes.string,
  }).isRequired,
};

export default Uploader;
